'use client'

import Link from 'next/link'
import {usePathname, useRouter} from 'next/navigation'
import {useTranslations} from 'next-intl'
import {LangSwitch} from '@/design-system'
import {APP_LOCALES} from '@/lib/locales'
import type {Locale} from '@/lib/api/types'

/**
 * The header a signed-out visitor sees on a public catalogue page (WEB-31).
 *
 * The wordmark goes to the landing page, the language switch is a plain
 * navigation to the same page in another locale (there is no account to
 * write the choice to), and the one call to action is signing in — which
 * returns the visitor to this page.
 */
export function PublicHeader({locale, signInHref}: {locale: Locale; signInHref: string}) {
  const t = useTranslations('shell')
  const pathname = usePathname()
  const router = useRouter()

  return (
    <header className="u-app__header">
      <div className="u-app__bar">
        <Link
          href={`/${locale}`}
          style={{
            fontFamily: 'var(--font-display)',
            fontWeight: 'var(--weight-extrabold)',
            fontSize: 'var(--text-lg)',
            color: 'var(--ink-1)',
            textDecoration: 'none'
          }}
        >
          Uniap
        </Link>

        <div style={{marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8}}>
          <LangSwitch
            value={locale}
            languages={APP_LOCALES}
            label={t('languageLabel')}
            onChange={(next: Locale) => {
              if (next !== locale) router.replace(pathname.replace(`/${locale}`, `/${next}`))
            }}
          />
          {/* A real link, not a router push: it leaves for Google's consent screen. */}
          <a
            href={signInHref}
            className="u-btn u-btn--primary u-btn--sm"
            data-testid="public-sign-in"
          >
            {t('signIn')}
          </a>
        </div>
      </div>
    </header>
  )
}
